import { Button, Drawer, Typography, Select } from 'antd';
import { SaveOutlined } from '@ant-design/icons';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNotification } from '../../../../Helper/NotificationProvider';
import { useSelector } from 'react-redux';
import TemplatePreview from '../../../TemplatePreview/TemplatePreview';

const ChairDetails = ({ layoutCopy, open, onClose, guest, selectedShapeId, event }) => {
  const { t } = useTranslation();
  const [detailsChair, setDetailsChair] = useState(guest);
  const { openNotification } = useNotification();
  const templates = useSelector(state => state.templates.data);
  const [selectedTemplate, setSelectedTemplate] = useState(detailsChair?.matching?.template || event?.templateName)

  const shape = useMemo(() => layoutCopy?.find(
    (chair) => chair.chairId === selectedShapeId
  ), [selectedShapeId])

  const isSpecial = shape?.shape?.special === true;

  useEffect(() => {
    setDetailsChair(guest);
    setSelectedTemplate(guest?.matching?.template || event?.templateName)
  }, [guest]);

  const onDrawerClose = async () => {
    await onClose();
    setDetailsChair(guest);
    setSelectedTemplate(guest?.matching?.template || event?.templateName)
  };

  const handleSave = () => {
    const newChair = {
      ...detailsChair,
        matching: {
          ...detailsChair.matching,
          template: selectedTemplate
        }
      }
    onClose(newChair, shape);
  };

  return (
    <Drawer
      title={t("Details")}
      onClose={onDrawerClose}
      open={open}
      width={600}
      destroyOnClose
      extra={
        <Button
          icon={<SaveOutlined />}
          onClick={handleSave}
          type="primary"
          disabled={!detailsChair}
        >
          {t('Speichern')}
        </Button>
      }
    >
      {!detailsChair ? (
        <Typography.Text type="secondary">{t('Kein Stuhl ausgewählt.')}</Typography.Text>
      ) : (<>
      {/* Chair ID */}
      <Typography.Title
        level={3}
        style={{
          marginBottom: 0,
          marginTop: 0
        }}
      >
        {t("Stuhl ID")}
      </Typography.Title>
      <Typography.Text
        copyable
        style={{
          margin: 0,
          fontSize: 20,
        }}
      >
        {guest?.chair}
      </Typography.Text>

      {/* Sitzplatznummer */}
      <Typography.Title
        level={3}
        style={{
          marginBottom: 0,
        }}
      >
        {t("Sitzplatznummer")}
      </Typography.Title>
      <Typography.Text
        style={{
          margin: 0,
          fontSize: 20,
        }}
      >
        {shape?.shape?.number}
      </Typography.Text>

      {/* Reihe */}
      <Typography.Title
        level={3}
        style={{
          marginBottom: 0,
        }}
      >
        {t("Reihe")}
      </Typography.Title>
      <Typography.Text
        style={{
          marginTop: 0,
          fontSize: 20,
        }}
      >
        {shape?.shape?.row}
      </Typography.Text>

      {/* Block */}
      <Typography.Title
        level={3}
        style={{
          marginBottom: 0,
        }}
      >
        {t("Block")}
      </Typography.Title>
      <Typography.Text
        style={{
          marginTop: 0,
          fontSize: 20,
        }}
      >
        {shape?.shape?.block}
      </Typography.Text>

      {/* Template */}
      <Typography.Title
        level={3}
        style={{
          marginBottom: 0,
        }}
      >
        {t("Template")}
      </Typography.Title>
      <Select
        style={{ minWidth: 150, marginTop: 10 }}
        showSearch
        placeholder="Template auswählen"
        value={selectedTemplate}
        onChange={(value, option) =>
          setSelectedTemplate(option.label)
        }
        filterOption={(input, option) =>
          (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
        }
        options={[
          ...new Map(templates.map((item) => [item.name, item])).values(),
        ].map(item => ({
          value: item.id,
          label: item.name,
        }))}
      />

      {(() => {
        const template = templates?.find(template => template?.name === selectedTemplate);

        return <>
          <div
            style={{marginTop: "10px"}}
          >
            <TemplatePreview 
              templateName={template?.name}
              sourceCode={template?.sourceCode} 
            />
          </div>
        </>
      })()}

      {/* Device ID */}
      <Typography.Title
        level={3}
        style={{
          marginBottom: 0,
        }}
      >
        {t("Label ID")}
      </Typography.Title>
      <Typography.Text
        copyable
        editable={
          !isSpecial
            ? {
              maxLength: 8,
              onChange: (newLabel) => {
                let err = ""
                if (newLabel.length !== 8) {
                  err = (t('Label ID muss exakt 8 Zeichen lang sein.'));
                } else if (!/^[0-9a-fA-F]{8}$/.test(newLabel)) {
                  err = (t('Label ID muss einen hexadezimalen Wert darstellen.'));
                }

                if(err !== "") {
                  openNotification(
                    'bottomRight',
                    t('Error'),
                    err,
                    false
                  );
                  return
                }

                setDetailsChair(() => ({
                  ...detailsChair,
                    matching: {
                      matched: true,
                      label: newLabel,
                    },
                }));
              },
            }
            : false
        }
        style={{
          margin: 0,
          fontSize: 20,
        }}
      >
        {detailsChair?.matching?.label || ''}
      </Typography.Text>


      {/* Vorname */}
      <Typography.Title
        level={3}
        style={{
          marginBottom: 0,
        }}
      >
        {t("Vorname")}
      </Typography.Title>
      <Typography.Text
        copyable
        editable={{
          onChange: (newGuestName) => {
            setDetailsChair((prev) => ({
              ...prev,
              name: newGuestName,
            }));
          },
          text: detailsChair.name || '',
        }}
        style={{
          margin: 0,
          fontSize: 20,
        }}
      >
        {detailsChair.name || ''}
      </Typography.Text>

      {/* Nachname */}
      <Typography.Title
        level={3}
        style={{
          marginBottom: 0,
        }}
      >
        {t("Nachname")}
      </Typography.Title>
      <Typography.Text
        copyable
        editable={{
          onChange: (newGuestName) => {
            setDetailsChair((prev) => ({
              ...prev,
              surname: newGuestName,
            }));
          },
          text: detailsChair.surname || '',
        }}
        style={{
          margin: 0,
          fontSize: 20,
        }}
      >
        {detailsChair.surname || ''}
      </Typography.Text>

      {/* Anrede */}
      <Typography.Title
        level={3}
        style={{
          marginBottom: 0,
        }}
      >
        {t("Anrede/Titel")}
      </Typography.Title>
      <Typography.Text
        copyable
        editable={{
          onChange: (title) => {
            setDetailsChair((prev) => ({
              ...prev,
              title: title,
            }));
          },
          text: detailsChair.title || '',
        }}
        style={{
          margin: 0,
          fontSize: 20,
        }}
      >
        {detailsChair.title || ''}
      </Typography.Text>

      {/* Company Info */}
      <Typography.Title
        level={3}
        style={{
          marginBottom: 0,
        }}
      >
        {t("Firma")}
      </Typography.Title>
      <Typography.Text
        copyable
        editable={{
          onChange: (newCompanyInfo) => {
            setDetailsChair((prev) => ({
              ...prev,
              companyInfo: newCompanyInfo,
            }));
          },
        }}
        style={{
          margin: 0,
          fontSize: 20,
        }}
      >
        {detailsChair.companyInfo || ''}
      </Typography.Text>

      {/* Message */}
      <Typography.Title
        level={3}
        style={{
          marginBottom: 0,
        }}
      >
        {t("Freifeld")}
      </Typography.Title>
      <Typography.Text
        copyable
        editable={{
          onChange: (newMessage) => {
            setDetailsChair((prev) => ({
              ...prev,
              message: newMessage,
            }));
          },
          text: detailsChair.message || '',
        }}
        style={{
          margin: 0,
          fontSize: 20,
        }}
      >
        {detailsChair.message || ''}
      </Typography.Text>

      {/* Custom Fields */}
      {guest?.matching?.customFields &&
        Object.entries(guest.matching.customFields).map(([key, value]) => (
          <div key={key}>
            <Typography.Title
              level={3}
              style={{
                marginBottom: 0,
                marginTop: 10
              }}
            >
              {t(key)} {/* Display field name */}
            </Typography.Title>
            <Typography.Text
              copyable
              editable={{
                onChange: (newValue) => {
                  setDetailsChair((prev) => ({
                    ...prev,
                      matching: {
                        ...prev.matching,
                        customFields: {
                          ...prev.matching.customFields,
                          [key]: newValue,
                        }
                      }
                  }));
                },
                text:  detailsChair.matching?.customFields?.[key] || '',
              }}
              style={{
                margin: 0,
                fontSize: 20,
              }}
            >
              {detailsChair.matching?.customFields?.[key]}
            </Typography.Text>
          </div>
        ))}
      </>)}
    </Drawer>
  );
};

export default React.memo(ChairDetails);
